/*
 * 商品选购弹框
 *
 * 功能：
 * 显示当前商品的详情
 * 切换规格根据规格显示SKU列表
 * 选购SKU数量时，控制起订量和库存
 * 规格列表显示选中数量的上标
 * 显示总数量、总价格
 * 控制起订量
 * 确认购物车数量的时候，发起请求
 * 商品移除购物车功能
 * 商品下单备注
 * 批量加购
 *
 * 事件：
 * 关闭模态框
 * 提交商品数据
 * 删除商品数据
 *
 *
 * 为什么要在data里存一个detail：(不在页面中直接使用，可以不用setData)
 * key为 encodeSpecs() 拼出来的规格标识，value内容同 saleProduct.details 中的项，允许数量为0
 * 初始化的时候，遍历 cloudProduct.productSkuModels 写入 detail ，每个SKU都在里面
 * 切换规格的时候，可以根据key快速找到对应规格一的SKU
 * 修改下单数量的时候，可以直接根据key找到对应的SKU，修改数量，判断库存
 * 确认数量下单的时候，detail中剔除数量为0的SKU，剩下的可以直接赋值给 saleProduct.details
 *
 *
 */

import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import useRefState from "@/hooks/useRefState";
import useCorpInfoStore from "@/stores/corp-info";
import useCartStore from "@/stores/cart";
import type {
  // ProductItem,
  UnitPriceItem,
  SpecItem,
  SpecElementItem,
  ProductSkuModelItem,
  // SaleProduct,
} from "@/types/product";
import type { PlaceOrderProps, SpecsItemDetail, batchWarnItem, ImageViewerItem } from "./types";
import { productUtil, utils, image as imgUtil, cal, apis, constant } from "@/utils";
import ProductCellPrice from "@/components/ProductCellPrice/ProductCellPrice";
// import Stepper from "@/components/Stepper";
import styles from "./PlaceOrder.module.less";
import useCorpSettingStore from "@/stores/corp-setting";
import iconRemark from "@/assets/img/icon/place_order_remark.png";
import { Toast } from "antd-mobile";
// import RadioList from '@/components/RadioList/RadioList'
import SpecRadio from "@/components/SpecRadio/SpecRadio";
import RemarkBox from "./RemarkBox";
import BatchWarn from "./BatchWarn";
import AddInput from "./AddInput";
// import ConvertOrderQuantity from '@/components/ConvertOrderQuantity/ConvertOrderQuantity'
import CustomizeImageViewerMulti from '@/components/CustomizeImageViewerMulti/CustomizeImageViewerMulti'
import { t } from "@/utils/i18n";
import Spec1 from "./Spec1";
import Total from "./Total";


const {
  product: { IMAGE_DEFAULT },
} = constant
const { cloneDeep, to, px2rem, concatenateImageUrl } = utils;
const { imgFormatter } = imgUtil;
const { formatPrice, getRecommendClass, getRecommendTag, encodeSpecs } =
  productUtil;
const { orderApi } = apis;
const PlaceOrder: React.FC<PlaceOrderProps> = memo((props) => {
  const { corpInfo } = useCorpInfoStore();
  const { corpId } = corpInfo;
  const { corpSetting } = useCorpSettingStore();
  const { cart, setCart } = useCartStore();
  const { cartId } = cart;
  const { qualityPrecision, cloudStoreStockType, stockOn, pricePrecision } =
    corpSetting;
  const { item: product, showPlaceOrder, onClosePlaceOrder, onRemoveCartProduct, onUpdateProd } = props;
  // const [product, setProduct] = useState<ProductItem>(item);
  const [show, setShow] = useState(false);
  const [thumb, setThumb] = useState("");
  const wrapperClassNames = `${styles["wrapper"]} ${show ? styles["active"] : ""}`;
  const [isMultiPrice] = useState((product.cloudProduct.productStatus & 128) === 128);
  const [chooseUnit, setChooseUnit, chooseUnitRef] = useRefState<UnitPriceItem>({
    unitIndex: 0,
    unitName: "",
    priceUnitName: "",
    productMoqQty: 0,
    price: 0,
    unitRatio: 1,
    originPrice: 0,
    linePrice: 0,
  });
  const [baseUnit, setBaseUnit] = useRefState<UnitPriceItem>({
    originPrice: 0,
    price: 0,
    linePrice: 0,
    unitRatio: 0,
    unitName: "",
  })
  const [specDetailList, setSpecDetailList] = useState<SpecItem[]>([]); // 多规格列表
  // const [spec1, setSpec1, spec1Ref] = useRefState<SpecsItemDetail[]>([]); // 当前规格一列表
  const [specId2, setSpecId2] = useState(""); // 当前规格二列表
  const [specId3, setSpecId3] = useState(""); // 当前规格三列表
  const [specBadge2, setSpecBadge2] = useState({}); // 规格二数字角标
  const [specBadge3, setSpecBadge3] = useState({}); // 规格三数字角标
  const [remark, setRemark] = useState("");
  const [currentRemark, setCurrentRemark] = useState(""); // 当前编辑的备注内容
  const [remarkModal, setRemarkModal] = useState(false);
  const [detail, setDetail, detailRef] = useRefState<{ [key: string]: SpecsItemDetail }>({}); // 当前选购的SKU列表，key使用encodeSpecs()拼起来，用于快速匹配
  const [addModal, setAddModal] = useState(false);
  const [batchWarn, setBatchWarn] = useState(false);
  const [batchWarnList, setBatchWarnList] = useState<batchWarnItem[]>([]);
  const [specElementUri, setSpecElementUri] = useState<ImageViewerItem[]>([]); // 所有 spu 图片集合
  const [editRemarkType, setEditRemarkType] = useState('product'); // 编辑的备注类型; product: 货品备注， sku: 规格备注
  const [editSpecItem, setEditSpecItem] = useState<SpecsItemDetail>(); // 编辑的规格备注索引
  const totalRef = useRef<any>(null);

  let loading = false; // 请求锁

  useEffect(() => {
    setShow(showPlaceOrder);
    if (showPlaceOrder) {
      init();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showPlaceOrder]);

  useEffect(() => {
    setBadge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, specId2, specId3]);

  // 初始化
  async function init() {
    await initThumb();
    initRemark();
    initUnit();
    initSpecList();
    initDetail();
  }

  // 初始化缩略图
  async function initThumb() {
    const { imageList, video } = product.cloudProduct;
    const imgUrl = (imageList && imageList[0]) || video;
    const [err, res] = await to(imgFormatter(imgUrl, 260));
    setThumb(err ? imgUrl : res);
  }

  // 初始化下单备注
  function initRemark() {
    const { saleProduct } = product;

    if (saleProduct && saleProduct.remark) {
      setRemark(saleProduct.remark);
    }
  }

  // 初始化单位
  function initUnit() {
    const { cloudProduct, saleProduct } = product;
    let unitIndex = cloudProduct.productMoqUnitIndex; // cloudProduct.defaultIndex;

    if (saleProduct && saleProduct.saleProductId) {
      unitIndex = saleProduct.details[0].unitIndex;
    }

    return changeUnit(unitIndex);
  }

  // 初始规格列表
  function initSpecList() {
    // 规格列表
    const specDetailList = product.cloudProduct.specDetailList || [];

    // 规格三、规格二
    let specId3 = "";
    let specId2 = "";

    if (specDetailList.length > 2) {
      specId3 = specDetailList[2].specElements[0].elementId;
    }

    if (specDetailList.length > 1) {
      specId2 = specDetailList[1].specElements[0].elementId;
    }
  
    initSpecElementUri(specDetailList);
    setSpecDetailList(specDetailList);
    setSpecId2(specId2);
    setSpecId3(specId3);
  }

  // 初始化 specElementUri spu 图片集合
  function initSpecElementUri(list: SpecItem[]) {
    const uriList: ImageViewerItem[] = [];
    for (let index = list.length - 1; index >= 0; index--) {
      const isHaveImg = list[index].specElements.findIndex((item: any) => {
        return item.uri
      }) > -1
      // 当前 spec 规格有上传图片
      if (isHaveImg) {
        list[index].specElements.forEach((element: any) => {
          uriList.push({
            desc: element.elementName,
            path: element.uri ? concatenateImageUrl(element.uri) : IMAGE_DEFAULT,
          });
        });
      }
    }

    setSpecElementUri(uriList);
  }

  // 初始detail结构
  function initDetail() {
    const { cloudProduct } = product;
    const { productSkuModels, skuDetails, specDetailList } = cloudProduct;
    const allSpecElements: { [key: string]: SpecElementItem } = {};
    const initProductDetail: { [key: string]: SpecsItemDetail } = {};

    // 多规格
    if (specDetailList) {
      specDetailList.forEach((spec: { specElements: any[] }) => {
        spec.specElements.forEach((element: SpecElementItem) => {
          allSpecElements[element.elementId] = element;
        });
      });
    }

    // 遍历cloudProduct生成detail
    (productSkuModels || skuDetails).forEach((sku: { elements: any; skuId?: any }) => {
      const { elements } = sku;
      const key = encodeSpecs(elements);

      // 多规格
      if (specDetailList) {
        elements.forEach((element: { uri: any; elementId: string | number }) => {
          element.uri = allSpecElements[element.elementId].uri;
        });
      }

      initProductDetail[key] = {
        elements: sku.elements,
        skuId: sku.skuId,
        stock: 0,
        quantity: 0,
      };
    });

    initSkuPrice(initProductDetail);
    updateDetailStock(initProductDetail);
    initDetailQty(initProductDetail);
    // loadQuantity(initProductDetail, specDetailList);
    setDetail(initProductDetail);
  }

  // 初始化sku价格
  function initSkuPrice(initProductDetail?: { [key: string]: SpecsItemDetail }) {
    const { cloudProduct } = product;
    const { productSkuModels, skuDetails } = cloudProduct;
    const cloneDetail = initProductDetail || detail;

    if (!isMultiPrice) {
      return;
    }

    // 遍历cloudProduct生成detail
    (productSkuModels || skuDetails).forEach((sku: ProductSkuModelItem) => {
      const key = encodeSpecs(sku.elements);
      const cloudPriceItem = (sku.cloudPriceList || []).find(
        (item: { unitIndex: any }) => item.unitIndex === chooseUnitRef.current.priceUnitIndex // 取基础单位价格
      );

      if (cloudPriceItem) {
        const saleProductSelectPrice = getSaleProductSelectPrice(sku.skuId);
        cloneDetail[key].price =
          saleProductSelectPrice === null
            ? formatPrice(cloudPriceItem.price, pricePrecision)
            : saleProductSelectPrice;

        cloneDetail[key].originPrice = cloudPriceItem.originPrice;
        cloneDetail[key].priceTypeId = cloudPriceItem.priceTypeId;
      }
    });

    if (!initProductDetail) {
      setDetail(cloneDetail);
    }
  }

  // 更新detail中的库存
  function updateDetailStock(initProductDetail?: { [key: string]: SpecsItemDetail }) {
    const { cloudProduct } = product;
    const { productSkuModels, skuDetails } = cloudProduct;
    const cloneDetail = initProductDetail || detail;
    let updateQty = false;
    const hasProductSkuModels = Boolean(productSkuModels);

    (productSkuModels || skuDetails).forEach((sku: ProductSkuModelItem) => {
      const key = encodeSpecs(sku.elements);

      // 如果 cloudStoreStockType === 1 ，库存要减去锁定库存
      let stock = hasProductSkuModels ? sku.skuStock : sku.stock;

      const skuLockStock = hasProductSkuModels ? sku.skuLockStock : sku.lockStock;

      if (cloudStoreStockType === 1 && sku.skuLockStock) {
        stock = cal.minus(stock, skuLockStock);
      }

      // 根据单位换算库存
      stock = cal.getDown(qualityPrecision, cal.divide(stock, chooseUnitRef.current.unitRatio));
      stock = Number(stock);
      // 修改库存
      cloneDetail[key].stock = stock;

      // 如果下单数量超过库存，要修改数量为库存数
      if (stockOn) {
        if (cloneDetail[key].quantity > stock) {
          if (stock >= 0) {
            // 正库存直接修改
            cloneDetail[key].quantity = stock;
            updateQty = true;
          } else if (cloneDetail[key].quantity > 0) {
            // 如果负库存则改为0
            cloneDetail[key].quantity = stock;
            updateQty = true;
          }
        }
      }
    });

    // 如果修改了数量，弹框提示
    if (updateQty) {
      Toast.show({
        content: t("库存不足，已修改下单数量"),
      });
    }

    if (!initProductDetail) {
      setDetail(cloneDetail);
      return;
    }
  }

  // 初始detail里的购买数量和 sku 备注
  function initDetailQty(initProductDetail?: { [key: string]: SpecsItemDetail }) {
    const { saleProduct } = product;
    const cloneDetail = initProductDetail || detail;

    if (saleProduct && saleProduct.saleProductId) {
      saleProduct.details.forEach((sku: { elements: any; quantity: number, skuRemark: string }) => {
        const key = encodeSpecs(sku.elements);
        if (cloneDetail[key]) {
          cloneDetail[key].quantity = sku.quantity;
          cloneDetail[key].skuRemark = sku.skuRemark;
        }
      });

      if (!initProductDetail) {
        setDetail(cloneDetail);
        // setBadge()
      }
    }
  }
  /*
   * 切换单位
   */
  // function onUnitSelected(index: number) {
  //   // const { index } = e.detail
  //   changeUnit(index)
  //   updateDetailStock()
  //   initSkuPrice()
  //   loadQuantity()
  // }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function changeUnit(_index: number) {
    const { cloudProduct } = product;
    const unitPriceArray = cloudProduct.unitPriceArray || [];
    // 设置基础单位
    setBaseUnit(unitPriceArray[0])
    const currentChooseUnit = productUtil.setChooseUnit(product, isMultiPrice, cloudStoreStockType, qualityPrecision, pricePrecision);
    setChooseUnit(currentChooseUnit);
  }

  // 获取已加购的skuId对应的价格
  function getSaleProductSelectPrice(skuId: string) {
    const { saleProduct } = product;

    if (saleProduct && saleProduct.details.length) {
      const detail = saleProduct.details.find(
        (item: { skuId: string; unitIndex: number }) =>
          item.skuId === skuId && item.unitIndex === chooseUnit.unitIndex
      );

      if (detail) {
        return detail.selectPrice;
      }
    }

    return null;
  }

  // 设置规格二、规格三的角标数量
  function setBadge() {
    const initSpecBadge3: { [key: string]: number } = {};
    const initSpecBadge2: { [key: string]: number } = {};
    const list: SpecsItemDetail[] = Object.values(detailRef.current);

    list.forEach((sku) => {
      // 三规格货品
      if (specDetailList.length === 3) {
        const spec3Id = sku.elements[2].elementId;
        const spec2Id = sku.elements[1].elementId;

        initSpecBadge3[spec3Id] = cal.plus(initSpecBadge3[spec3Id] || 0, sku.quantity);

        // 规格二仅统计当前选中的规格三
        if (spec3Id === specId3) {
          initSpecBadge2[spec2Id] = cal.plus(initSpecBadge2[spec2Id] || 0, sku.quantity);
        }
      }

      // 二规格货品
      if (specDetailList.length === 2) {
        const spec2Id = sku.elements[1].elementId;

        initSpecBadge2[spec2Id] = cal.plus(initSpecBadge2[spec2Id] || 0, sku.quantity);
      }
    });

    setSpecBadge2(initSpecBadge2);
    setSpecBadge3(initSpecBadge3);
  }

  /*
   * 切换规格二、规格三时，更新规格一中对应的购买数量、库存、sku缩略图
   */
  // function loadQuantity(
  //   initProductDetail?: { [key: string]: SpecsItemDetail },
  //   initSpecDetailList?: SpecItem[] | undefined
  // ) {
  //   const activeDetail = initProductDetail || detail;
  //   const activeSpecDetailList = initSpecDetailList || specDetailList;
  //   let currentSpec1: SpecsItemDetail[] = []; // 当前规格一

  //   if (activeSpecDetailList && activeSpecDetailList.length > 0) {
  //     // 多规格，循环一次规格一
  //     activeSpecDetailList[0].specElements.forEach((spec1Item: { elementId: any }) => {
  //       // 根据几级规格拼装 和encodeSpecs()一致 的 specsId
  //       let specsId = `SPECS-${spec1Item.elementId}`;

  //       if (activeSpecDetailList.length >= 2) {
  //         specsId = `${specsId}-${specId2Ref.current || activeSpecDetailList[1].specElements[0].elementId}`;
  //       }

  //       if (activeSpecDetailList.length >= 3) {
  //         specsId = `${specsId}-${specId3Ref.current || activeSpecDetailList[2].specElements[0].elementId}`;
  //       }

  //       // 直接去detail里匹配对应的值
  //       currentSpec1.push(activeDetail[specsId]);
  //     });
  //   } else {
  //     // 无规格，只有一个SKU
  //     currentSpec1 = [activeDetail["SPECS-"]];
  //   }

  //   // 查找规格1的缩略图
  //   currentSpec1.forEach((specItem) => {
  //     specItem.elements.forEach((elementItem) => {
  //       activeSpecDetailList.forEach((specDetailItem) => {
  //         specDetailItem.specElements.forEach((specElementItem) => {
  //           if (specElementItem.elementId === elementItem.elementId && specElementItem.uri) {
  //             elementItem.uri = specElementItem.uri;
  //           }
  //         });
  //       });
  //     });
  //   });

  //   setSpec1(currentSpec1);
  // }

  function handleClosePlaceOrder() {
    onClosePlaceOrder();
  }

  // 货品主图预览
  async function previewImg() {
    const { imageList, video } = product.cloudProduct;
    const images = []
    if (imageList && imageList.length > 0) {
      imageList.forEach((item: string) => {
        images.push({ path: concatenateImageUrl(item), desc: '' })
      })
    }
    
    if (!!imageList && video) {
      const [err, res] = await to(imgFormatter(video));
      if (err) return
      images.push({ path: res, desc: '' })
    }

    CustomizeImageViewerMulti({
      images,
    })
  }
  /*
   * 批量加购
   */
  function toggleAddModal() {
    setAddModal(!addModal);
  }

  function closeBatchWarnModal() {
    setBatchWarn(false);
    setBatchWarnList([]);
  }

  function confirmBatchAdd(num: number = 0) {
    toggleAddModal();

    if (num) {
      const warnArr: batchWarnItem[] = []; // 库存不足的规格
      let batchWarn = false;
      const productMoqQty = chooseUnit.productMoqQty;

      specDetailList[0].specElements.forEach((spec1Item) => {
        // 根据几级规格拼装和encodeSpecs()一致的specsId
        let specsId = `SPECS-${spec1Item.elementId}`;
        let preElementName = "";
        if (specDetailList.length >= 2) {
          specsId = `${specsId}-${specId2}`;
          const spec = specDetailList[1].specElements.find((item) => item.elementId === specId2);
          preElementName = `${spec ? spec.elementName : ""}，`;
        }
        if (specDetailList.length >= 3) {
          specsId = `${specsId}-${specId3}`;
          const spec = specDetailList[2].specElements.find((item) => item.elementId === specId3);
          preElementName = `${spec ? spec.elementName : ""}，${preElementName}`;
        }

        let { stock } = detail[specsId];

        stock = stock > 0 ? stock : 0;


        // 不允许负库存销售
        if (stockOn) {
          if (num < stock && num >= productMoqQty) { // 输入数 < 库存数 > 并且 输入数 >= 起订量，直接输入数
            detail[specsId].quantity = num;
          } else if (num >= stock && stock >= productMoqQty) { // 输入数 >= 库存数 >= 起订量，输入最大库存数
            detail[specsId].quantity = stock;
            // 输入数量大于库存才显示警告
            if (num > stock) {
              warnArr.push({
                elementName: `${preElementName}${spec1Item.elementName}`,
                num: stock,
                unit: chooseUnit.unitName || "",
              });
            }
          } else if (num < productMoqQty && productMoqQty <= stock) { // 输入数<起订量<=库存数，自动修改为起订量
            detail[specsId].quantity = productMoqQty;
          } else {
            warnArr.push({
              elementName: `${preElementName}${spec1Item.elementName}`,
              num: 0,
              unit: chooseUnit.unitName || "",
            });
          }
        } else { // 可以负库存销售
          // 输入数<起订量，直接填入起订量 or 输入数>起订量，直接输入数
          detail[specsId].quantity = Math.max(num, productMoqQty);
        }
      });

      // 如果有超出库存的，弹出提示
      if (warnArr.length) {
        batchWarn = true;
      }

      // 修改数量
      setDetail(cloneDeep(detail));
      setBatchWarn(batchWarn);
      setBatchWarnList(warnArr);
      // loadQuantity(detail);
    }
  }

  // 切换规格一
  const changeSpecElementsValue1 = useCallback((sku: SpecsItemDetail) => {
    const currentDetail = detailRef.current;
    currentDetail[encodeSpecs(sku.elements)] = sku;
    setDetail(cloneDeep(currentDetail));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 切换规格二
  function changeSpecElementsValue2(value: SpecElementItem) {
    const activeSpecId2 = value.elementId;
    // 更新规格二中的选中项
    setSpecId2(activeSpecId2);
    // loadQuantity();

    // 如果有规格图则显示
    // const img = value.uri;

    // if (img) {
    //   setThumb(concatenateImageUrl(img));
    // }
  }

  // 切换规格三
  function changeSpecElementsValue3(value: SpecElementItem) {
    const astiveSpecId3 = value.elementId;
    // 更新规格三中的选中项
    setSpecId3(astiveSpecId3);
    // loadQuantity();

    // 如果有规格图则显示
    // const img = value.uri;

    // if (img) {
    //   setThumb(concatenateImageUrl(img));
    // }
  }

  // 移出购物车
  async function removeBill() {
    const { saleProduct } = product;

    if (loading) return;

    // 如果本来就不在购物车里，减到0不需要额外操作
    if (!saleProduct || !saleProduct.saleProductId) {
      handleClosePlaceOrder();
      return;
    }

    // 否则请求删除购物车delete接口
    Toast.show({
      icon: "loading",
      content: t("加载中…"),
      duration: 0,
    });
    loading = true;

    const [err, res] = await to(
      orderApi.deleteProd({
        corpId: saleProduct.corpId,
        cartId: saleProduct.cartId,
        saleProductIds: [saleProduct.saleProductId],
      })
    );

    loading = false;
    Toast.clear();

    if (err) return;

    // 更新 store cart
    const cartData = res.data;
    if (cartData) {
      cart.productCount = cartData.productCount;
      setCart(cart);
    }

    handleClosePlaceOrder();
    onRemoveCartProduct();

    // 关闭模态框
    Toast.show({
      content: t("删除成功"),
      duration: 2000,
    });
  }

  // 确定按钮
  async function confirmBill() {
    if (totalRef.current?.disable) return

    const { cloudProduct, saleProduct } = product;
    const unitPriceArray = cloudProduct.unitPriceArray || [];
    const details: any[] = [];

    Object.values(detail).forEach((sku: any) => {
      if (sku.quantity > 0) {
        const { unitIndex, unitName, unitRatio, priceIndex } = chooseUnit;
        sku.priceUnitIndex = unitPriceArray[0].unitIndex;
        sku.priceUnitName = unitPriceArray[0].unitName;
        sku.priceUnitRatio = unitPriceArray[0].unitRatio;
        sku.priceIndex = priceIndex;
        sku.unitIndex = unitIndex;
        sku.unitName = unitName;
        sku.unitRatio = unitRatio;
        sku.priceTypeId = chooseUnit.priceTypeId;
        sku.selectPrice = getSelectPrice(sku);
  
        // 不同规格不同价格
        if (isMultiPrice) {
          sku.price = sku.originPrice;
        } else {
          sku.price = chooseUnit.originPrice;
        }
    
        details.push(sku);
      }
    });

    // 添加规格图片
    details.forEach((item) => {
      const { uri: image } = item.elements.find((item: { uri: any }) => item.uri) || {};

      if (image) {
        item.image = image;
      }
    });

    if (loading) return;

    // 如果总数量是0，则调用移除购物车函数
    if (totalRef.current?.totalQuantity <= 0) {
      removeBill();
      return;
    }

    // 否则请求修改购物车add_update接口
    const params = {
      saleProduct: {
        cartId,
        corpId,
        details,
        remark,
        productId: cloudProduct.productId,
        saleProductId: saleProduct ? saleProduct.saleProductId : null,
        selected: 1,
      },
    };

    Toast.show({
      icon: "loading",
      content: t("加载中…"),
      duration: 0,
    });

    loading = true;
    const [err, res] = await to(orderApi.addUpdate(params, {showMessage: false}));
    loading = false;
    Toast.clear();
    
    if (err) {
      Toast.show(err.message || err.msg);
      return
    }

    // 更新 cart 角标
    cart.productCount = res.data.productCount;
    setCart(cart);

    handleClosePlaceOrder();
    onUpdateProd(res.data.saleProduct);
  }

  // 获取selectPrice
  function getSelectPrice(sku: SpecsItemDetail) {
    const { cloudProduct } = product;
    const unitPriceArray = cloudProduct.unitPriceArray || [];
    const { skuId } = sku;

    const saleProductSelectPrice = getSaleProductSelectPrice(skuId);

    if (saleProductSelectPrice !== null) {
      return saleProductSelectPrice;
    }

    if (isMultiPrice) {
      return formatPrice(sku.price || 0, pricePrecision);
    }

    return formatPrice(unitPriceArray[0].price, pricePrecision);
  }

  function toggleRemarkModal() {
    setRemarkModal(!remarkModal);
  }

  function confirmRemark(detail: string) {
    if (editRemarkType === 'product') {
      setRemark(detail.slice(0, 1000));
    } else {
      handleSetSkuRemark(detail.slice(0, 1000))
    }

    setRemarkModal(false);
  }

  function previewSpuImg(img: string, name?: string) {
    const defaultIndex = specElementUri.findIndex((item) => {
      if (img === IMAGE_DEFAULT && name) {
        return item.desc.indexOf(name) > -1
      }
      return item.path.indexOf(img) > -1
    });

    CustomizeImageViewerMulti({
      defaultIndex: defaultIndex === -1 ? 0 : defaultIndex,
      images: specElementUri
    })
  }

  function onEditSkuRemark(sku: SpecsItemDetail) {
    setCurrentRemark(sku.skuRemark || '')
    setEditSpecItem(sku);
    setEditRemarkType('sku')
    toggleRemarkModal();
  }

  function handleSetSkuRemark(value: string) {
    const currentDetail = detailRef.current;
    if (editSpecItem) {
      editSpecItem.skuRemark = value;
      currentDetail[encodeSpecs(editSpecItem.elements)] = editSpecItem;
    }
  }

  // 编辑货品备注
  function handleEditProductRemark() {
    setCurrentRemark(remark)
    setEditRemarkType('product')
    toggleRemarkModal()
  }

  return (
    <div className={styles["place-order-wrap"]}>
      <div className={wrapperClassNames}>
        <div className={styles["place-order-modal-mask"]} onClick={handleClosePlaceOrder}></div>
        <div className={styles["place-order-modal"]}>
          {/* 顶部内容框 */}
          <div className={styles["modal-product-brief"]}>
            <div className={styles["header-box"]}>
              <img
                className={styles["preview-box"]}
                src={thumb}
                onClick={() => previewImg()}
              />
              <div className={styles["info-box"]}>
                <div className={styles["title"] + ' ep-2'}>
                  <span className={getRecommendClass(product.cloudProduct)}>
                    {getRecommendTag(product.cloudProduct)}
                  </span>
                  <span>{product.cloudProduct?.productName || ""}</span>
                </div>
                <div>
                  {chooseUnit.productMoqQty > 0 && (
                    <div className={styles["modal-product-quantity"]}>
                      MOQ：{chooseUnit.productMoqQty}
                      {chooseUnit.unitName}
                    </div>
                  )}

                  <div className={styles["modal-product-price"]}>
                    <ProductCellPrice
                      index={0}
                      lineHeightType={1}
                      value={chooseUnit.priceText}
                      unit={chooseUnit.priceUnitName || ""}
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* 右上角关闭按钮  */}
            <div
              className={styles["icon-wrap"] + " weui-flex__item"}
              onClick={handleClosePlaceOrder}
            >
              <div className={"close-icon " + styles["place-order-close"]}></div>
            </div>
          </div>

          {/* 内容操作部分  */}
          <div className={styles["place-order-scrollable-detail"]}>
            {/* 单位切换  */}
            {/* {
               product.cloudProduct?.unitPriceArray &&
               product.cloudProduct?.unitPriceArray.length > 1 ? 
                 <div className={styles['weui-cell'] + ' ' + styles['unit-wrapper']}>
                   <div className={styles['type-name']}>单位</div>
                   <RadioList
                     radioList={product.cloudProduct?.unitPriceArray}
                     itemName={'unitName'}
                     itemKey={'unitId'}
                     checked={chooseUnit.unitIndex || 0}
                     onChange={onUnitSelected}
                   />
               </div> : ''
              } */}

            {/* 多规格加购框  */}
            <div className={styles["specificationtable-wrapper"]}>
              {/* 渲染第3个规格  */}
              {specDetailList.length > 2 && (
                <div style={{ marginBottom: px2rem(32) }}>
                  <div className={styles["type-name"]}>
                    {specDetailList[2].specName}
                  </div>
                  <SpecRadio
                    itemName={"elementName"}
                    itemKey={"elementId"}
                    radioList={specDetailList[2].specElements}
                    checked={specId3}
                    badgeList={specBadge3}
                    nowrap={true}
                    onChange={changeSpecElementsValue3}
                    previewImg={previewSpuImg}
                  />
                </div>
              )}

              {/* 渲染第2个规格  */}  
              {specDetailList.length > 1 && (
                <div className={styles["kp-second-specification-wrapper"]}>
                  <div className={styles["type-name"]}>
                    {specDetailList[1].specName}
                  </div>
                  <SpecRadio
                    itemName={"elementName"}
                    itemKey={"elementId"}
                    radioList={specDetailList[1].specElements}
                    checked={specId2}
                    badgeList={specBadge2}
                    nowrap={false}
                    onChange={changeSpecElementsValue2}
                    previewImg={previewSpuImg}
                  />
                </div>
              )}
              {/* 渲染第1个规格 和 无规格 */}
              <Spec1
                specName={specDetailList.length ? specDetailList[0].specName : ''}
                detail={detail}
                specId2={specId2}
                specId3={specId3}
                specDetailList={specDetailList}
                isMultiPrice={isMultiPrice}
                chooseUnit={chooseUnit}
                unitPriceArray={product.cloudProduct?.unitPriceArray || []}
                productMoqUnitIndex={product.cloudProduct?.productMoqUnitIndex}
                changeSpecElementsValue1={changeSpecElementsValue1}
                previewImg={previewSpuImg}
                onEditSkuRemark={onEditSkuRemark}
                onToggleAddModal={toggleAddModal}
              />
            </div>
            {/* remark  */}
            { specDetailList.length === 0 &&
              (<div className={styles["remark-wrapper"]} onClick={handleEditProductRemark}>
                <p className={styles["remark-text"]}>
                  {remark ? remark : t("添加备注")}
                  <img src={iconRemark} className={styles["remark-icon"]} />
                </p>
              </div>)
            }
            {/* 占位框  */}
            <div className={styles["fixed-bottom"]}></div>
          </div>
          <Total
            ref={totalRef}
            chooseUnit={chooseUnit}
            details={detail}
            baseUnit={baseUnit}
            isMultiPrice={isMultiPrice}
            productMoqUnitIndex={product.cloudProduct?.productMoqUnitIndex}
            productPrice={product.cloudProduct?.unitPriceArray ? product.cloudProduct?.unitPriceArray[product.cloudProduct?.productMoqUnitIndex].price : chooseUnitRef.current.price}
            onConfirmBill={confirmBill}
          />
          {/* <div className={styles["confirm-button-wrapper"]}>
            {!!product.saleProduct && (
              <div
                className={styles["confirm-button"] + " " + styles["confirm-button-remove"]}
                hover-class="remove-btn-hover"
                onClick={removeBill}
              >
                {t("移出购物车")}
              </div>
            )}
            <button
              className={styles["confirm-button"]}
              hover-class="sure-btn-hover"
              onClick={confirmBill}
            >
              {t("确认")}
            </button>
          </div> */}
        </div>
      </div>
      {remarkModal && (
        <RemarkBox
          status={remarkModal}
          value={currentRemark}
          confirm={confirmRemark}
          cancel={toggleRemarkModal}
        />
      )}
      {addModal && <AddInput status={addModal} onConfirm={confirmBatchAdd} onClose={toggleAddModal} />}
      {batchWarn && <BatchWarn warnList={batchWarnList} onClose={closeBatchWarnModal} />}
    </div>
  );
});

export default PlaceOrder;
